// src/components/forms/MappingBuilder.tsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSave,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  AggregatedTextField,
  AuthorInfoFields,
  CheckBoxMappingValue,
  ObjectMapping,
  PdfTemplateMapping,
  TextFieldDefault,
  TextFieldMappingValue,
} from "../../../../interfaces/dynamicForms/PdfMapping";
import { useAuth } from "../../../../firebase/AuthProvider";
import AuthorInfoEditor from "./AuthorInfoEditor";
import TextFieldDefaultsEditor from "./TextFieldDefaultsEditor";
import {
  getMappingConfiguration,
  saveMappingConfiguration,
} from "../../../../services/formsService/FormMappingService";
import AggregatedTextFieldsEditor from "./AggregatedTextFieldsEditor";
import CheckboxMappingsEditor from "./CheckBoxMappingsEditor";
import JsonSyntaxHighlighter from "../../../../utilities/formattedDisplayComponents/jsonSyntaxHighlighter";
import TextFieldMappingsEditor from "./TextFieldMappingEditor";
import ObjectMappingsEditor from "./ObjectMappingEditor";
import { getSpecificConfig } from "../../../../services/formsService/FormBuilderService";
import { FormDefinition } from "../../../../interfaces/dynamicForms/FormBuilder";

// Define a default empty structure
const initialMapping: PdfTemplateMapping = {
  // Initialize all expected properties, even if empty, for type safety
  CheckBoxMappings: {},
  CheckBoxConditions: [],
  TextFieldMappings: {},
  TextFieldDefaults: [],
  AggregatedTextFields: [],
  ConditionalTextFields: [],
  ObjectMappings: [],
  AuthorInfoFields: { SCFullName: "", SCID: "", SupervisorFullName: "", ProgramManagerFullName: "", TaskType: "", OrganizationName: "" }, // Provide initial object
};

const MappingBuilder: React.FC = () => {
  const { mappingKey: editMappingKey } = useParams<{ mappingKey?: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [mappingConfig, setMappingConfig] = useState<PdfTemplateMapping | null>(
    null
  );
  const [formDefinition, setFormDefinition] = useState<FormDefinition | null>(null);
  const [currentMappingKey, setCurrentMappingKey] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  // --- Load Mapping useEffect (Keep as before) ---
  useEffect(() => {
    // ... existing loadMapping logic ...
    const loadMapping = async (key: string) => {
      setIsLoading(true);
      setLoadError(null);
      // Start with initial structure to avoid null checks everywhere later
      setMappingConfig({ ...initialMapping });
      setCurrentMappingKey(key);
      setSaveSuccess(false);

      if (!currentUser) {
        setLoadError("Authentication required.");
        setIsLoading(false);
        return;
      }
      const token = await currentUser.getIdToken();

      try {
        const [mappingJson, formDefResponse] = await Promise.all([
          getMappingConfiguration(key, token),
          getSpecificConfig(key, "CONFIG", token)
        ]);

        if (mappingJson) {
          const parsedMapping = JSON.parse(mappingJson) as PdfTemplateMapping;
          // Merge fetched data with initial structure to ensure all keys exist
          setMappingConfig((current) => ({
            ...(current ?? initialMapping),
            ...parsedMapping,
          }));
        } else {
          // ... error handling ...
          setMappingConfig(null);
        }
        if (formDefResponse && formDefResponse[key]) {
          setFormDefinition(formDefResponse[key]);
        } else {
            console.warn(`Could not load form definition for key: ${key}`);
            setFormDefinition(null);
        }
      } catch {}
      // ... finally ...
    };
    if (editMappingKey) {
      loadMapping(editMappingKey);
    } else {
      setMappingConfig(initialMapping); /* ... */
    }
  }, [editMappingKey, currentUser]);

  // --- State Update Callbacks ---

  // Callback for AuthorInfoEditor
  const handleAuthorInfoChange = useCallback((newFields: AuthorInfoFields) => {
    setMappingConfig((current) => {
      if (!current) return null; // Should not happen if initialized correctly
      return { ...current, AuthorInfoFields: newFields };
    });
    setSaveSuccess(false); // Clear success message on change
  }, []); // Doesn't depend on external state other than setMappingConfig

  // Callback for TextFieldDefaultsEditor
  const handleTextFieldDefaultsChange = useCallback(
    (newDefaults: TextFieldDefault[]) => {
      setMappingConfig((current) => {
        if (!current) return null;
        return { ...current, TextFieldDefaults: newDefaults };
      });
      setSaveSuccess(false);
    },
    []
  );

  const handleAggregatedTextFieldsChange = useCallback(
    (newAggregations: AggregatedTextField[]) => {
      setMappingConfig((current) =>
        current ? { ...current, AggregatedTextFields: newAggregations } : null
      );
      setSaveSuccess(false);
    },
    []
  );

  const handleCheckboxMappingsChange = useCallback(
    (newMappings: Record<string, CheckBoxMappingValue>) => {
      setMappingConfig((current) =>
        current ? { ...current, CheckBoxMappings: newMappings } : null
      );
      setSaveSuccess(false);
    },
    []
  );

  const handleTextFieldMappingsChange = useCallback(
    (newMappings: Record<string, TextFieldMappingValue>) => {
      setMappingConfig((current) =>
        current ? { ...current, TextFieldMappings: newMappings } : null
      );
      setSaveSuccess(false);
    },
    []
  );

  const handleObjectMappingsChange = useCallback(
    (newMappings: ObjectMapping[]) => {
      setMappingConfig((current) =>
        current ? { ...current, ObjectMappings: newMappings } : null
      );
      setSaveSuccess(false);
    },
    []
  );

  // --- Other Callbacks (handleSave, handleMappingKeyChange - Keep as before) ---
  const handleSave = useCallback(async () => {
    if (!mappingConfig || !currentUser) {
      setSaveError("Mapping data or user not available.");
      return;
    }
    if (!editMappingKey && !currentMappingKey) {
      setSaveError(
        "Mapping Key / Identifier cannot be empty when creating a new mapping."
      );
      return;
    }

    const keyToSave = editMappingKey || currentMappingKey; // Use edited key or new key

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    const token = await currentUser.getIdToken();

    try {
      // Pass the mapping object, service will stringify
      await saveMappingConfiguration(keyToSave, mappingConfig, token);
      setSaveSuccess(true);
      // Optionally navigate back or disable further edits until refresh?
      // If creating, update the URL? navigate(`/form/mapping/edit/${keyToSave}`, { replace: true });
      if (!editMappingKey) {
        // If we just created it, navigate to the edit url
        navigate(`/form/mapping/edit/${keyToSave}`, { replace: true });
      }
    } catch (error) {
      console.error("Failed to save mapping:", error);
      setSaveError(
        error instanceof Error
          ? error.message
          : "An unknown error occurred during saving."
      );
    } finally {
      setIsSaving(false);
    }
  }, [mappingConfig, currentUser, currentMappingKey, editMappingKey, navigate]);

  const handleMappingKeyChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCurrentMappingKey(event.target.value);
    setSaveSuccess(false); // Clear success message on change
    setSaveError(null);
  };

  // --- Render Logic ---
  const handleBack = () => navigate("/form");

  if (isLoading)
    return (
      <div>
        Loading Mapping... <FontAwesomeIcon icon={faSpinner} spin />
      </div>
    );
  if (loadError)
    return (
      <div className="text-red-600">Error loading mapping: {loadError}</div>
    );
  // If mappingConfig is null even after loading attempts, show error
  if (!mappingConfig)
    return (
      <div className="text-red-600">
        Mapping data could not be loaded or initialized. Load Error:{" "}
        {loadError ?? "Unknown reason."}
      </div>
    );

  return (
    <div className="bg-white">
    <div className="container mx-auto p-4 md:p-8">
      <button
        onClick={handleBack}
        className="mb-4 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
      >
        <FontAwesomeIcon
          icon={faArrowLeft}
          className="-ml-1 mr-2 h-5 w-5"
          aria-hidden="true"
        />
        Back to Forms List
      </button>{" "}
      <h1 className="text-2xl font-bold mb-4">
        {editMappingKey
          ? `Edit Mapping: ${editMappingKey}`
          : "Create New Mapping"}
      </h1>
      <div className="mb-4 max-w-md">
        <label
          htmlFor="mappingKey"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Mapping Key / Identifier
        </label>
        <input
          type="text"
          id="mappingKey"
          value={currentMappingKey}
          onChange={handleMappingKeyChange}
          readOnly={!!editMappingKey} // Read-only when editing
          placeholder={editMappingKey ? "" : "e.g., MyForm_Mapping.json"}
          className={`block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm ${
            editMappingKey
              ? "bg-gray-100 text-gray-500 cursor-not-allowed"
              : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
          }`}
        />
        {!editMappingKey && (
          <p className="text-xs text-gray-500 mt-1">
            Enter a unique identifier for this mapping (often ending in .json).
          </p>
        )}
      </div>
      {/* --- Mapping Editor Sections --- */}
      <div className="p-4 border rounded bg-gray-50 mb-4">
        <h2 className="text-lg font-semibold mb-2">Mapping Editor UI</h2>
        <p className="text-sm text-gray-600">
          (Build dedicated UI components for each section: TextFields,
          Checkboxes, Conditions, Objects, etc.)
        </p>

        {/* Render Author Info Editor */}
        <AuthorInfoEditor
          fields={mappingConfig.AuthorInfoFields}
          onChange={handleAuthorInfoChange}
        />

        {/* Render Text Field Defaults Editor */}
        <TextFieldDefaultsEditor
          defaults={mappingConfig.TextFieldDefaults}
          updateDefaults={handleTextFieldDefaultsChange}
        />

        {/* Render Aggregated Text Fields Editor */}
        <AggregatedTextFieldsEditor
          aggregations={mappingConfig.AggregatedTextFields}
          updateAggregations={handleAggregatedTextFieldsChange}
        />

        {/* Render Checkbox Mappings Editor */}
        <CheckboxMappingsEditor
          mappings={mappingConfig.CheckBoxMappings}
          updateMappings={handleCheckboxMappingsChange}
        />

        <TextFieldMappingsEditor
          mappings={mappingConfig.TextFieldMappings}
          updateMappings={handleTextFieldMappingsChange}
        />

        <ObjectMappingsEditor
          mappings={mappingConfig.ObjectMappings}
          updateMappings={handleObjectMappingsChange}
          formDefinition={formDefinition}
        />

      </div>
      {/* --- Raw JSON Viewer (for reference/debugging) --- */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold mb-2">Current Mapping JSON</h2>
        <pre className="bg-gray-800 text-white p-4 rounded-md overflow-x-auto text-sm max-h-96">
          <JsonSyntaxHighlighter
            jsonString={JSON.stringify(mappingConfig, null, 2)}
          />
        </pre>
      </div>
      {/* --- Save Area --- */}
      <div className="mt-6 p-4 border-t">
        {saveSuccess && (
          <div className="mb-3 p-3 text-sm text-green-800 bg-green-100 rounded border border-green-300">
            Mapping saved successfully!
          </div>
        )}
        {saveError && (
          <div className="mb-3 p-3 text-sm text-red-800 bg-red-100 rounded border border-red-300">
            Error saving: {saveError}
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={isSaving || (!editMappingKey && !currentMappingKey)} // Disable if no key in create mode
          className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <FontAwesomeIcon
            icon={isSaving ? faSpinner : faSave}
            className={`${isSaving ? "animate-spin" : ""} -ml-1 mr-3 h-5 w-5`}
            aria-hidden="true"
          />
          {isSaving ? "Saving..." : "Save Mapping"}
        </button>
      </div>
    </div>
    </div>
  );
};

export default MappingBuilder;
